import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaEdit, FaTrash, FaImage } from 'react-icons/fa';
import Swal from 'sweetalert2';
import cpApi from '../../utils/cpApi';
import { BASE_URL } from '../../utils/api';
import './AddCollege.css';

const API_BASE = BASE_URL;

// Map backend fields → form fields
const toForm = (col) => ({
  name:    col.name    || '',
  title:   col.titleHeader   || '',
  code:    col.code    || '',
  email:   col.email   || '',
  contact: col.contactNumber || '',
  address: col.address || '',
  terms:   col.termCondition || '',
  collegeId: col.collegeId || '',
  collegePassword: '',
  clerkName: col.clerkName || '',
  clerkId: col.clerkId || '',
  clerkPassword: '',
  hasCollegePassword: col.hasCollegePassword || false,
  hasClerkPassword: col.hasClerkPassword || false,
  receiptType: col.receiptType || 'receipt', // NEW FIELD Added
});

const EMPTY_FORM = { 
  name: '', title: '', code: '', email: '', contact: '', address: '', terms: '',
  collegeId: '', collegePassword: '',
  clerkName: '', clerkId: '', clerkPassword: '',
  hasCollegePassword: false, hasClerkPassword: false,
  receiptType: 'receipt' // Default value for new colleges
};

const AddCollege = () => {
  const [colleges, setColleges]             = useState([]);
  const [loading, setLoading]               = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showAddForm, setShowAddForm]       = useState(false);
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [logoFile, setLogoFile]             = useState(null);
  const [previewLogo, setPreviewLogo]       = useState(null);
  const [editId, setEditId]                 = useState(null);
  const [expandedRow, setExpandedRow]       = useState(null);

  // New states for password change toggles
  const [changeCollegePwd, setChangeCollegePwd] = useState(false);
  const [changeClerkPwd, setChangeClerkPwd]     = useState(false);

  const searchRef = useRef(null);

  // ── Load colleges from backend ──────────────────────────────────────────────
  const loadColleges = async () => {
    setLoading(true);
    try {
      const { data } = await cpApi.get('/controlpanel/colleges');
      setColleges(data.data);
    } catch {
      Swal.fire('Error', 'Failed to load colleges.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadColleges(); }, []);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target))
        setShowSearchDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setPreviewLogo(URL.createObjectURL(file));
  };

  const validateForm = () => {
    const required = ['name', 'code', 'email', 'contact', 'address', 'clerkId', 'receiptType'];
    if (required.some(f => !form[f])) {
      Swal.fire('Missing Fields', 'Please fill all required fields (including Clerk ID)!', 'warning');
      return false;
    }
    const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailReg.test(form.email)) {
      Swal.fire('Invalid Email', 'Please enter a valid college email address.', 'warning');
      return false;
    }
    if (!emailReg.test(form.clerkId)) {
      Swal.fire('Invalid Clerk Email', 'Clerk Login ID must be a valid email address.', 'warning');
      return false;
    }
    if (form.collegeId && !emailReg.test(form.collegeId)) {
      Swal.fire('Invalid College Email', 'College Login ID must be a valid email address.', 'warning');
      return false;
    }
    if (!editId && !form.clerkPassword) {
      Swal.fire('Missing Password', 'Clerk Password is required for new colleges.', 'warning');
      return false;
    }
    if (editId && changeClerkPwd && !form.clerkPassword) {
      Swal.fire('Missing Password', 'Please enter the new Clerk Password.', 'warning');
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setLogoFile(null);
    setPreviewLogo(null);
    setEditId(null);
    setShowAddForm(false);
    setChangeCollegePwd(false);
    setChangeClerkPwd(false);
  };

  // ── Save (create or update) ──────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      // Build FormData — backend uses multipart for logo upload
      const fd = new FormData();
      fd.append('name',          form.name);
      fd.append('titleHeader',   form.title);    
      fd.append('code',          form.code.toUpperCase());
      fd.append('email',         form.email);
      fd.append('contactNumber', form.contact);  
      fd.append('address',       form.address);
      fd.append('termCondition', form.terms);    
      fd.append('receiptType',   form.receiptType); // NEW FIELD
      
      // Append Login details
      fd.append('collegeId', form.collegeId);
      if (form.collegePassword) fd.append('collegePassword', form.collegePassword);
      
      fd.append('clerkName', form.clerkName);
      fd.append('clerkId', form.clerkId);
      if (form.clerkPassword) fd.append('clerkPassword', form.clerkPassword);

      if (logoFile) fd.append('logo', logoFile);

      const opts = { headers: { 'Content-Type': 'multipart/form-data' } };

      if (editId) {
        const { data } = await cpApi.put(`/controlpanel/colleges/${editId}`, fd, opts);
        setColleges(prev => prev.map(c => c._id === editId ? data.data : c));
        Swal.fire('Updated!', 'College information has been updated.', 'success');
      } else {
        const { data } = await cpApi.post('/controlpanel/colleges', fd, opts);
        setColleges(prev => [data.data, ...prev]);
        Swal.fire('Added!', 'New college has been added successfully.', 'success');
      }
      resetForm();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save college.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Edit ─────────────────────────────────────────────────────────────────────
  const handleEdit = (col) => {
    setEditId(col._id);
    setForm(toForm(col));
    setLogoFile(null);
    setPreviewLogo(col.logo ? `${API_BASE}/${col.logo}` : null);
    setShowAddForm(true);
    setExpandedRow(null);
    setChangeCollegePwd(false);
    setChangeClerkPwd(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Delete ───────────────────────────────────────────────────────────────────
  const handleDelete = (col) => {
    Swal.fire({
      title: `Delete "${col.name}"?`,
      text: 'This will permanently delete the college and its logo.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#95a5a6',
      confirmButtonText: 'Yes, delete it',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await cpApi.delete(`/controlpanel/colleges/${col._id}`);
        setColleges(prev => prev.filter(c => c._id !== col._id));
        Swal.fire('Deleted!', 'College has been deleted.', 'success');
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || 'Delete failed.', 'error');
      }
    });
  };

  // ── Toggle Active/Disabled ───────────────────────────────────────────────────
  const toggleStatus = async (col) => {
    try {
      const { data } = await cpApi.patch(`/controlpanel/colleges/${col._id}/toggle`);
      setColleges(prev => prev.map(c => c._id === col._id ? data.data : c));
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to update status.', 'error');
    }
  };

  // ── Logo URL helper ──────────────────────────────────────────────────────────
  const getLogoUrl = (col) => col.logo ? `${API_BASE}/${col.logo}` : null;

  // ── Search filter ────────────────────────────────────────────────────────────
  const displayedColleges = useMemo(() => {
    if (!searchQuery) return colleges;
    const q = searchQuery.toLowerCase();
    return colleges.filter(c =>
      c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [colleges, searchQuery]);

  return (
    <div className="add-college-in-control-panel-container">
      <h2 className="add-college-in-control-panel-title">Colleges</h2>

      {/* ── Controls ── */}
      <div className="add-college-in-control-panel-controls">
        <div className="add-college-in-control-panel-search-wrapper" ref={searchRef}>
          <FaSearch className="add-college-in-control-panel-search-icon" />
          <input
            type="text"
            className="add-college-in-control-panel-search-input"
            placeholder="Search by college name or code..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowSearchDropdown(true); }}
            onFocus={() => setShowSearchDropdown(true)}
          />
          {showSearchDropdown && searchQuery && (
            <div className="add-college-in-control-panel-search-dropdown">
              <ul>
                {displayedColleges.length > 0 ? displayedColleges.map(c => (
                  <li key={c._id} onClick={() => { setSearchQuery(c.name); setShowSearchDropdown(false); }}>
                    {c.name} ({c.code})
                  </li>
                )) : (
                  <div className="add-college-in-control-panel-search-empty">No colleges found</div>
                )}
              </ul>
            </div>
          )}
        </div>

        <button
          className="add-college-in-control-panel-btn add-college-in-control-panel-btn-add"
          onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}
        >
          <FaPlus /> {showAddForm ? 'Close Form' : 'Add College'}
        </button>
      </div>

      {/* ── Add / Edit Form ── */}
      {showAddForm && (
        <div className="add-college-in-control-panel-inline-form">
          <h3 className="add-college-in-control-panel-form-title">
            {editId ? '✏️ Edit College' : 'Add New College'}
          </h3>

          {/* 1. Logo Upload */}
          <div className="add-college-in-control-panel-logo-upload-wrapper">
            <div className="add-college-in-control-panel-logo-preview">
              {previewLogo
                ? <img src={previewLogo} alt="Preview" />
                : <FaImage className="add-college-in-control-panel-placeholder-icon" />}
            </div>
            <div className="add-college-in-control-panel-upload-controls">
              <label className="add-college-in-control-panel-upload-btn">
                {previewLogo ? 'Change Logo' : 'Upload Logo'}
                <input type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
              </label>
              <span className="add-college-in-control-panel-upload-hint">JPG, PNG, WebP — max 5 MB</span>
              {previewLogo && (
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: '0.78rem', cursor: 'pointer', padding: 0, marginTop: 4 }}
                  onClick={() => { setLogoFile(null); setPreviewLogo(null); }}
                >
                  ✕ Remove logo
                </button>
              )}
            </div>
          </div>

          {/* 2. Core College Information */}
          <div className="add-college-in-control-panel-form-grid">
            <div className="add-college-in-control-panel-form-group">
              <label>College Name <span className="add-college-in-control-panel-req">*</span></label>
              <input type="text" name="name" value={form.name} onChange={handleInputChange} placeholder="Enter full college name" />
            </div>
            <div className="add-college-in-control-panel-form-group">
              <label>College Title / Header <span style={{fontSize:'0.75rem',color:'#94a3b8',fontWeight:400}}>(optional)</span></label>
              <input type="text" name="title" value={form.title} onChange={handleInputChange} placeholder="e.g. Empowering Education" />
            </div>
            <div className="add-college-in-control-panel-form-group">
              <label>College Code <span className="add-college-in-control-panel-req">*</span></label>
              <input type="text" name="code" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. UNI001" />
            </div>
            <div className="add-college-in-control-panel-form-group">
              <label>Email Address <span className="add-college-in-control-panel-req">*</span></label>
              <input type="email" name="email" value={form.email} onChange={handleInputChange} placeholder="admin@college.edu" />
            </div>
            <div className="add-college-in-control-panel-form-group">
              <label>Contact Number <span className="add-college-in-control-panel-req">*</span></label>
              <input type="text" name="contact" value={form.contact} onChange={handleInputChange} placeholder="Phone number" />
            </div>
            <div className="add-college-in-control-panel-form-group">
              <label>Full Address <span className="add-college-in-control-panel-req">*</span></label>
              <input type="text" name="address" value={form.address} onChange={handleInputChange} placeholder="Street, City, State, Zip" />
            </div>

            {/* NEW FIELD: Fee Receipt Numbering Style */}
            <div className="add-college-in-control-panel-form-group add-college-in-control-panel-full-width">
              <label>Fee Receipt Numbering Style <span className="add-college-in-control-panel-req">*</span></label>
              <div className="add-college-in-control-panel-radio-group">
                <label className="add-college-in-control-panel-radio-label">
                  <input 
                    type="radio" 
                    name="receiptType" 
                    value="receipt" 
                    checked={form.receiptType === 'receipt'} 
                    onChange={handleInputChange} 
                  />
                  Receipt Number (CollegeCode/Session/001 — Resets every session)
                </label>
                <label className="add-college-in-control-panel-radio-label">
                  <input 
                    type="radio" 
                    name="receiptType" 
                    value="serial" 
                    checked={form.receiptType === 'serial'} 
                    onChange={handleInputChange} 
                  />
                  Serial Number (01, 02... 1401 — Continuous, never resets)
                </label>
              </div>
            </div>
          </div>

          <div className="add-college-in-control-panel-form-group add-college-in-control-panel-full-width" style={{ marginBottom: '24px' }}>
            <label>Terms and Conditions</label>
            <textarea name="terms" value={form.terms} onChange={handleInputChange} rows="3" placeholder="Enter specific terms and conditions for this college..." />
          </div>

          {/* 3. College Login (Optional) */}
          <h4 className="add-college-in-control-panel-sub-title">College Login (Optional)</h4>
          <div className="add-college-in-control-panel-form-grid" style={{ marginBottom: '24px' }}>
            <div className="add-college-in-control-panel-form-group">
              <label>College Login Email</label>
              <input type="email" name="collegeId" value={form.collegeId} onChange={handleInputChange} placeholder="college@example.com" />
            </div>
            <div className="add-college-in-control-panel-form-group">
              <label>
                Password
                {editId && changeCollegePwd && (
                  <span className="add-college-in-control-panel-cancel-pwd" onClick={() => { setChangeCollegePwd(false); setForm(f => ({...f, collegePassword: ''})); }}>Cancel</span>
                )}
              </label>
              {editId && !changeCollegePwd && form.hasCollegePassword ? (
                <button type="button" className="add-college-in-control-panel-btn-change-pwd" onClick={() => setChangeCollegePwd(true)}>Change Password</button>
              ) : (
                <input type="text" name="collegePassword" value={form.collegePassword} onChange={handleInputChange} placeholder={editId ? "Enter new password" : "Enter password"} />
              )}
            </div>
          </div>

          {/* 4. Clerk Login (Required) */}
          <h4 className="add-college-in-control-panel-sub-title">Clerk Login <span className="add-college-in-control-panel-req">*</span></h4>
          <div className="add-college-in-control-panel-form-grid">
            <div className="add-college-in-control-panel-form-group">
              <label>Clerk Name <span style={{fontSize:'0.75rem',color:'#94a3b8',fontWeight:400}}>(optional)</span></label>
              <input type="text" name="clerkName" value={form.clerkName} onChange={handleInputChange} placeholder="Enter Clerk Name" />
            </div>
            <div className="add-college-in-control-panel-form-group">
              <label>Clerk Login Email <span className="add-college-in-control-panel-req">*</span></label>
              <input type="email" name="clerkId" value={form.clerkId} onChange={handleInputChange} placeholder="clerk@example.com" />
            </div>
            <div className="add-college-in-control-panel-form-group">
              <label>
                Password <span className="add-college-in-control-panel-req">*</span>
                {editId && changeClerkPwd && (
                  <span className="add-college-in-control-panel-cancel-pwd" onClick={() => { setChangeClerkPwd(false); setForm(f => ({...f, clerkPassword: ''})); }}>Cancel</span>
                )}
              </label>
              {editId && !changeClerkPwd && form.hasClerkPassword ? (
                <button type="button" className="add-college-in-control-panel-btn-change-pwd" onClick={() => setChangeClerkPwd(true)}>Change Password</button>
              ) : (
                <input type="text" name="clerkPassword" value={form.clerkPassword} onChange={handleInputChange} placeholder={editId ? "Enter new password" : "Enter password"} />
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="add-college-in-control-panel-form-buttons">
            <button className="add-college-in-control-panel-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="add-college-in-control-panel-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update College' : 'Save College'}
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading colleges...</div>
      ) : (
        <div className="add-college-in-control-panel-table-wrapper">
          <table className="add-college-in-control-panel-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th className="add-college-in-control-panel-align-left">College Name</th>
                <th className="add-college-in-control-panel-table-code">Code</th>
                <th className="add-college-in-control-panel-table-email">Email</th>
                <th className="add-college-in-control-panel-table-status">Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedColleges.length === 0 ? (
                <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No colleges found.</td></tr>
              ) : displayedColleges.map((col, i) => (
                <React.Fragment key={col._id}>
                  <tr className="add-college-in-control-panel-main-row">
                    <td>{i + 1}</td>
                    <td className="add-college-in-control-panel-align-left">
                      <div
                        className="add-college-in-control-panel-name-wrapper"
                        onClick={() => setExpandedRow(expandedRow === col._id ? null : col._id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span>{col.name}</span>
                        <button className="add-college-in-control-panel-expand-btn">
                          {expandedRow === col._id ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                    </td>
                    <td className="add-college-in-control-panel-table-code">
                      <span className="add-college-in-control-panel-badge-code">{col.code}</span>
                    </td>
                    <td className="add-college-in-control-panel-table-email">{col.email || '—'}</td>
                    <td className="add-college-in-control-panel-table-status">
                      <label className="add-college-in-control-panel-switch">
                        <input type="checkbox" checked={col.isActive} onChange={() => toggleStatus(col)} />
                        <span className="add-college-in-control-panel-slider" />
                      </label>
                      <div style={{ fontSize: '0.7rem', marginTop: '4px', color: col.isActive ? '#27ae60' : '#e74c3c', fontWeight: '600' }}>
                        {col.isActive ? 'Active' : 'Disabled'}
                      </div>
                    </td>
                    <td>
                      <div className="add-college-in-control-panel-action-btns">
                        <button className="add-college-in-control-panel-btn-edit" onClick={() => handleEdit(col)} title="Edit"><FaEdit /></button>
                        <button className="add-college-in-control-panel-btn-delete" onClick={() => handleDelete(col)} title="Delete"><FaTrash /></button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Row */}
                  {expandedRow === col._id && (
                    <tr className="add-college-in-control-panel-expanded-row">
                      <td colSpan="6">
                        <div className="add-college-in-control-panel-expanded-content">

                          {/* Logo — always in expand (not in table) */}
                          <div className="add-college-in-control-panel-expanded-item">
                            <span className="add-college-in-control-panel-exp-label">Logo</span>
                            <span className="add-college-in-control-panel-exp-value">
                              {getLogoUrl(col)
                                ? <img
                                    src={getLogoUrl(col)}
                                    alt={col.name}
                                    style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', border: '1px solid #e2e8f0' }}
                                    onError={e => { e.target.style.display = 'none'; }}
                                  />
                                : <div className="add-college-in-control-panel-logo-placeholder" style={{ width: 56, height: 56 }}>{col.name[0]}</div>}
                            </span>
                          </div>

                          {/* Title Header — always in expand (never in table) */}
                          <div className="add-college-in-control-panel-expanded-item">
                            <span className="add-college-in-control-panel-exp-label">Title Header</span>
                            <span className="add-college-in-control-panel-exp-value">{col.titleHeader || '—'}</span>
                          </div>

                          {/* Contact — always in expand (never in table) */}
                          <div className="add-college-in-control-panel-expanded-item">
                            <span className="add-college-in-control-panel-exp-label">Contact</span>
                            <span className="add-college-in-control-panel-exp-value">{col.contactNumber || '—'}</span>
                          </div>
                          
                          {/* Login Credentials Summaries */}
                          <div className="add-college-in-control-panel-expanded-item">
                            <span className="add-college-in-control-panel-exp-label">College ID</span>
                            <span className="add-college-in-control-panel-exp-value">{col.collegeId || '—'}</span>
                          </div>
                          <div className="add-college-in-control-panel-expanded-item">
                            <span className="add-college-in-control-panel-exp-label">Clerk ID</span>
                            <span className="add-college-in-control-panel-exp-value">{col.clerkId || '—'}</span>
                          </div>
                          <div className="add-college-in-control-panel-expanded-item">
                            <span className="add-college-in-control-panel-exp-label">Clerk Name</span>
                            <span className="add-college-in-control-panel-exp-value">{col.clerkName || '—'}</span>
                          </div>

                          {/* Code — show in expand ONLY when hidden from table (< 480px) */}
                          <div className="add-college-in-control-panel-expanded-item add-college-in-control-panel-exp-code">
                            <span className="add-college-in-control-panel-exp-label">College Code</span>
                            <span className="add-college-in-control-panel-exp-value">{col.code}</span>
                          </div>

                          {/* Email — show in expand ONLY when hidden from table (< 768px) */}
                          <div className="add-college-in-control-panel-expanded-item add-college-in-control-panel-exp-email">
                            <span className="add-college-in-control-panel-exp-label">Email</span>
                            <span className="add-college-in-control-panel-exp-value">{col.email || '—'}</span>
                          </div>

                          {/* Status — show in expand ONLY when hidden from table (< 480px) */}
                          <div className="add-college-in-control-panel-expanded-item add-college-in-control-panel-exp-status">
                            <span className="add-college-in-control-panel-exp-label">Status</span>
                            <span className="add-college-in-control-panel-exp-value">
                              <label className="add-college-in-control-panel-switch">
                                <input type="checkbox" checked={col.isActive} onChange={() => toggleStatus(col)} />
                                <span className="add-college-in-control-panel-slider" />
                              </label>
                              <div style={{ fontSize: '0.7rem', marginTop: '4px', color: col.isActive ? '#27ae60' : '#e74c3c', fontWeight: '600' }}>
                                {col.isActive ? 'Active' : 'Disabled'}
                              </div>
                            </span>
                          </div>

                          {/* Address — always in expand (never in table) */}
                          <div className="add-college-in-control-panel-expanded-item add-college-in-control-panel-exp-half">
                            <span className="add-college-in-control-panel-exp-label">Address</span>
                            <span className="add-college-in-control-panel-exp-value">{col.address || '—'}</span>
                          </div>

                          {/* NEW FIELD IN EXPAND: Receipt Style - ALWAYS visible in Expand View */}
                          <div className="add-college-in-control-panel-expanded-item add-college-in-control-panel-exp-half">
                            <span className="add-college-in-control-panel-exp-label">Receipt Numbering Style</span>
                            <span className="add-college-in-control-panel-exp-value">
                              {col.receiptType === 'serial' ? 'Serial Number (Continuous)' : 'Receipt Number (Resets per Session)'}
                            </span>
                          </div>

                          {/* Terms — always in expand (never in table) */}
                          <div className="add-college-in-control-panel-expanded-item add-college-in-control-panel-exp-full">
                            <span className="add-college-in-control-panel-exp-label">Terms and Conditions</span>
                            <span className="add-college-in-control-panel-exp-value" style={{ fontWeight: 400, color: '#555' }}>{col.termCondition || '—'}</span>
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AddCollege;